import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { toDisplayPrompt } from "~/shared/lib/element-mentions";
import { useChatSettingsStore } from "~/shared/store/useChatSettingsStore";
import { useDraftChatStore } from "~/shared/store/useDraftChatStore";

import type { MentionableElement } from "~/shared/lib/element-mentions";
import type { PromptPrefill } from "~/shared/store/types";

/** Вход применения заявки: чат, состояние черновика и две операции промпт-бара. */
export interface UseRepeatPrefillParams {
    chatId: string | null;
    /** В баре уже что-то набрано (текст, вложения или элементы) — спросим. */
    hasDraft: boolean;
    setText: (value: string) => void;
    /** Сброс pending-вложений чата: повтор заменяет кадры, а не добавляет к ним. */
    clearAttachments: () => void;
    /** Библиотека элементов: даёт актуальные имена для упоминаний в промпте. */
    library?: readonly MentionableElement[];
    /** Библиотека уже загружена. Пока `false`, «элемента нет» значит «ещё не приехал». */
    libraryLoaded?: boolean;
}

/**
 * Потребляет заявку «повторить запрос» и заполняет промпт-бар.
 *
 * Два обязательных условия фичи:
 *  1. Ничего не затираем молча — если в баре уже что-то набрано, применение
 *     ждёт подтверждения человека (диалог рисует `PromptBox`).
 *  2. Порядок применения — сначала модель и режим, потом кадры и элементы:
 *     слоты ролей и лимит элементов приходят ОТ режима, и в обратном порядке
 *     кадры легли бы в пул, которого в целевом режиме нет.
 *
 * Генерацию не запускает: заполнили бар и остановились.
 *
 * Отдельно сообщает об элементах запроса, которых больше нет в библиотеке: чипом
 * такой элемент не встанет и в новую генерацию не поедет, а промпт покажет его
 * имя из снимка — без предупреждения человек отправил бы не тот запрос.
 */
export function useRepeatPrefill({
    chatId,
    hasDraft,
    setText,
    clearAttachments,
    library = [],
    libraryLoaded = false,
}: UseRepeatPrefillParams) {
    const prefill = useDraftChatStore((s) => (chatId ? s.prefillByChat[chatId] : undefined));
    const clearPrefill = useDraftChatStore((s) => s.clearPrefill);
    const addPending = useDraftChatStore((s) => s.addPending);
    const setElementIds = useDraftChatStore((s) => s.setElementIds);
    const applyPreset = useChatSettingsStore((s) => s.applyPreset);

    const [confirming, setConfirming] = useState<PromptPrefill | null>(null);
    // Снимок элементов применённой заявки. Сверку с библиотекой откладываем до
    // её загрузки: считать «удалённым» всё подряд, пока список едет, нельзя.
    const [appliedElements, setAppliedElements] = useState<PromptPrefill | null>(null);
    // Референсы прошлого запроса повтор не переносит (канал не исполним ни одним
    // подключённым провайдером) — держим их число, чтобы сказать об этом человеку.
    const [droppedReferenceImages, setDroppedReferenceImages] = useState(0);

    // Через ref: наличие черновика проверяется в момент прихода заявки, но само
    // по себе (набрал букву в промпте) не должно перезапускать эффект.
    const hasDraftRef = useRef(hasDraft);
    hasDraftRef.current = hasDraft;

    const apply = useCallback(
        (value: PromptPrefill) => {
            if (!chatId) return;
            applyPreset(value.settings);
            // Промпт приходит в хранимом виде (ссылки по id) — в поле кладём
            // имена. Актуальное имя из библиотеки перекрывает снимок: повторяем
            // мы новый запрос, и уйдёт в него сегодняшнее имя элемента.
            const known = new Set(library.map((element) => element.id));
            setText(
                toDisplayPrompt(value.prompt, [
                    ...(value.elementNames ?? []).filter((element) => !known.has(element.id)),
                    ...library,
                ]),
            );
            clearAttachments();
            if (value.attachments.length > 0) addPending(chatId, value.attachments);
            setElementIds(chatId, value.elementIds);
            setAppliedElements(value.elementIds.length > 0 ? value : null);
            setDroppedReferenceImages(value.droppedReferenceImages);
        },
        [chatId, applyPreset, setText, clearAttachments, addPending, setElementIds, library],
    );

    useEffect(() => {
        if (!chatId || !prefill) return;
        // Заявка одноразовая — снимаем её сразу, до применения: иначе повторный
        // рендер (или возврат в чат) применил бы тот же запрос ещё раз.
        clearPrefill(chatId);
        if (hasDraftRef.current) {
            setConfirming(prefill);
            return;
        }
        apply(prefill);
    }, [chatId, prefill, clearPrefill, apply]);

    // Ушли в другой чат с открытым вопросом — заявка уже снята, спрашивать не о чем.
    useEffect(() => {
        setConfirming(null);
        setAppliedElements(null);
        setDroppedReferenceImages(0);
    }, [chatId]);

    /** Элементы применённого запроса, которых больше нет в библиотеке. */
    const missingElements = useMemo(() => {
        if (!appliedElements || !libraryLoaded) return [];
        const known = new Set(library.map((element) => element.id));
        const nameById = new Map((appliedElements.elementNames ?? []).map((e) => [e.id, e.name]));
        return appliedElements.elementIds
            .filter((id) => !known.has(id))
            .map((id) => ({ id, name: nameById.get(id) ?? id }));
    }, [appliedElements, libraryLoaded, library]);

    /** Снять предупреждения повтора: и про потерянные элементы, и про референсы. */
    const dismissRepeatNotices = useCallback(() => {
        setAppliedElements(null);
        setDroppedReferenceImages(0);
    }, []);

    const confirm = useCallback(async () => {
        if (confirming) apply(confirming);
        setConfirming(null);
    }, [confirming, apply]);

    const cancel = useCallback(() => setConfirming(null), []);

    return {
        confirmOpen: confirming !== null,
        confirm,
        cancel,
        missingElements,
        droppedReferenceImages,
        dismissRepeatNotices,
    };
}
